import { useState } from 'react';
import { Button, Sheet, SheetContent, SheetDescription, SheetHeader, SheetTitle, Textarea } from '@@/ui/primitives';
import { ChevronLeftIcon, ChevronRightIcon, Edit3Icon } from 'lucide-react';
import { useUpdateItem, useGetItemReferences } from '../../hooks/use-items';
import { ItemRecord } from '../../api/api-items';
import { EntityRecord } from '../../api/api-entities';
import { ItemEditor } from './item-editor';
import { ItemViewer } from './item-viewer';
import { ItemReferencesSidebar } from './item-references-sidebar';
import { clsx } from 'clsx';
import { FieldValues } from 'react-hook-form';
import { Spinner } from '@@/ui/components';
import { toast } from 'sonner';

interface ItemViewSheetProps {
  item: ItemRecord | null;
  entity: EntityRecord | null;
  isOpen: boolean;
  onOpenChange: (open: boolean) => void;
}

export function ItemViewSheet({ item, entity, isOpen, onOpenChange }: ItemViewSheetProps) {
  const [isEditing, setIsEditing] = useState(false);
  const [showReferences, setShowReferences] = useState(false);
  const { mutateAsync: updateItem, isPending: isUpdatePending } = useUpdateItem();

  const { data: references, isLoading: isLoadingReferences } = useGetItemReferences(
    item?.entityId ?? '',
    item?.itemId ?? '',
    { enabled: !!item && isOpen }
  );

  const handleSave = async (values: FieldValues) => {
    if (!item) return;

    try {
      await updateItem({
        entity: item.entityId,
        path: item.itemId,
        data: { attributes: values }
      });
      setIsEditing(false);
      onOpenChange(false);
    } catch (error: unknown) {
      if (error instanceof Error) {
        toast.error(error.message);
      } else {
        toast.error('An unknown error occurred while creating the item.');
      }
    }
  };

  const formatDate = (dateString: string) => {
    const date = new Date(dateString);
    return date.toLocaleDateString('en-US', {
      year: 'numeric',
      month: 'short',
      day: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    });
  };

  const totalReferences = references ? references.referencedBy.length : 0;

  const handleClose = () => {
    setIsEditing(false);
    setShowReferences(false);
    onOpenChange(false);
  };

  return (
    <Sheet open={isOpen} onOpenChange={handleClose}>
      <SheetContent
        className={clsx(
          'overflow-auto font-sans',
          'flex flex-row gap-0',
          showReferences ? 'min-w-200' : 'min-w-100',
          'duration-150!'
        )}
      >
        {/* References Sidebar */}
        {showReferences && references && (
          <ItemReferencesSidebar isLoading={isLoadingReferences} referencedBy={references.referencedBy} />
        )}
        {item && entity && (
          <div className='flex flex-col min-w-0 flex-1 max-w-100'>
            {/* Header */}
            <SheetHeader className='bg-slate-100'>
              <div className='flex flex-col gap-4'>
                <div className='flex flex-col gap-1'>
                  <span className='text-xs! text-slate-600 font-[450] truncate leading-none! mb-3 flex items-center'>
                    {item.itemId}
                  </span>
                  <SheetTitle className='text-slate-950'>{item.primaryKey}</SheetTitle>
                  <SheetDescription className='text-slate-600 font-[450] text-xs flex items-center gap-1'>
                    Created on {formatDate(item.timestamp)}
                  </SheetDescription>
                </div>
              </div>
            </SheetHeader>

            {/* Content */}
            <div className='flex-1 flex gap-1 flex-col overflow-hidden mt-2'>
              <div className='flex items-center gap-2 justify-between px-3'>
                <Button
                  variant='outline'
                  size='sm'
                  disabled={isLoadingReferences || totalReferences === 0}
                  onClick={() => setShowReferences(!showReferences)}
                  className='text-sm! leading-snug font-medium! px-3!'
                >
                  {showReferences ? <ChevronRightIcon className='-ml-px!' /> : <ChevronLeftIcon className='-ml-px!' />}
                  References
                  {isLoadingReferences ? <Spinner className='mt-px' /> : null}
                </Button>
                {!isEditing && (
                  <Button
                    variant='outline'
                    size='sm'
                    onClick={() => setIsEditing(true)}
                    className='text-sm! leading-snug font-medium! px-3!'
                  >
                    <Edit3Icon className='-ml-px!' />
                    Edit
                  </Button>
                )}

                {isEditing && (
                  <>
                    <Button
                      variant='outline'
                      size='sm'
                      onClick={() => setIsEditing(false)}
                      className='text-sm! leading-snug font-medium! px-3!'
                    >
                      Cancel
                    </Button>
                  </>
                )}
              </div>
              <div className='flex-1 overflow-auto'>
                {entity && entity.schema && entity.schema.fields && Object.keys(entity.schema.fields).length > 0 ? (
                  <div className='space-y-4'>
                    {isEditing ? (
                      <ItemEditor
                        entity={entity}
                        item={item}
                        mode='edit'
                        onSubmit={handleSave}
                        className='space-y-4'
                        formId='item-view-form'
                        isSubmitting={isUpdatePending}
                      />
                    ) : (
                      <ItemViewer entity={entity} item={item} />
                    )}
                  </div>
                ) : (
                  <div className='space-y-6'>
                    {/* Metadata Grid */}
                    <div className='grid grid-cols-2 gap-6 mb-6'>
                      <div>
                        <h4 className='font-semibold text-neutral-900 mb-2'>Item ID</h4>
                        <p className='font-mono text-sm bg-neutral-100 p-2 rounded'>{item.itemId}</p>
                      </div>
                      <div>
                        <h4 className='font-semibold text-neutral-900 mb-2'>Entity ID</h4>
                        <p className='font-mono text-sm bg-neutral-100 p-2 rounded'>{item.entityId}</p>
                      </div>
                    </div>

                    {/* Raw JSON Editor */}
                    <div>
                      <h4 className='font-semibold text-neutral-900 mb-2'>Attributes</h4>
                      {isEditing ? (
                        <Textarea
                          defaultValue={JSON.stringify(item.attributes, null, 2)}
                          className='h-64 font-mono text-sm'
                          placeholder='JSON attributes...'
                        />
                      ) : (
                        <div className='bg-neutral-100 p-4 rounded font-mono text-sm overflow-auto max-h-64'>
                          <pre>{JSON.stringify(item.attributes, null, 2)}</pre>
                        </div>
                      )}
                    </div>
                  </div>
                )}
              </div>
            </div>
          </div>
        )}
        {/* </div> */}
      </SheetContent>
    </Sheet>
  );
}
